import React, { useMemo, useState } from "react";
import {
  Box,
  Paper,
  Typography,
  Divider,
  LinearProgress,
  IconButton,
  Tooltip,
  Chip,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import ExpandLessRounded from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import PassagemPipelineCard from "./PassagemPipelineCard";
import { formatBRL } from "../../utils/utils";

export default function PassagemPipelineStage({
  stage,
  cards,
  totalCards,
  onOpenVenda,
  onOpenLead,
  onRecover,
  recoverableStageIds = [],
}) {
  const theme = useTheme();
  const [collapsed, setCollapsed] = useState(false);
  const [visible, setVisible] = useState(12);

  const pct = useMemo(() => {
    const t = totalCards || 1;
    return Math.min(100, Math.round(((cards?.length || 0) / t) * 100));
  }, [cards, totalCards]);

  const stats = useMemo(() => {
    const list = cards || [];
    const totalValue = list.reduce((s, c) => s + (Number(c.value) || 0), 0);
    const count = list.length;
    const avgTicket = count ? totalValue / count : 0;
    return { totalValue, count, avgTicket };
  }, [cards]);

  const visibleCards = (cards || []).slice(0, collapsed ? 0 : visible);
  const restLocal = Math.max(0, (cards?.length || 0) - visibleCards.length);

  return (
    <Paper
      sx={{
        backgroundColor: theme.palette.background.default,
        borderRadius: 2,
        boxShadow: "0px 4px 6px rgba(0,0,0,0.08)",
        padding: 1.25,
        minWidth: 300,
        maxWidth: 340,
        display: "flex",
        flexDirection: "column",
        border: `1px solid ${theme.palette.divider}`,
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: theme.palette.primary.main, mr: 1 }}>
          {stage.name} ({cards?.length || 0})
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={collapsed ? "Expandir" : "Recolher"}>
          <IconButton size="small" onClick={() => setCollapsed((v) => !v)}>
            {collapsed ? <ExpandMoreRounded /> : <ExpandLessRounded />}
          </IconButton>
        </Tooltip>
      </Box>

      {!collapsed && (
        <>
          <LinearProgress
            variant="determinate"
            value={pct}
            sx={{
              height: 4,
              borderRadius: 3,
              mb: 1,
              backgroundColor: theme.palette.action.hover,
              "& .MuiLinearProgress-bar": { backgroundColor: theme.palette.primary.main },
            }}
          />
          <Box sx={{ display: "flex", gap: 0.75, flexWrap: "wrap", mb: 1 }}>
            <Chip size="small" variant="outlined" label={`Valor: ${formatBRL(stats.totalValue)}`} />
            <Chip size="small" variant="outlined" label={`Ticket médio: ${formatBRL(stats.avgTicket)}`} />
            <Chip
              size="small"
              variant="outlined"
              label={`${stats.count} item${stats.count === 1 ? "" : "s"}`}
            />
          </Box>
        </>
      )}

      <Divider />

      <Box
        sx={{
          flex: 1,
          minHeight: 80,
          mt: 1.5,
          maxHeight: "calc(100vh - 260px)",
          overflowY: "auto",
          pr: 0.5,
        }}
      >
        {!collapsed &&
          visibleCards.map((card) => (
            <PassagemPipelineCard
              key={card.id}
              card={card}
              onOpenVenda={onOpenVenda}
              onOpenLead={onOpenLead}
              onRecover={onRecover}
              recoverable={recoverableStageIds.includes(stage.id)}
            />
          ))}

        {!collapsed && (cards?.length || 0) === 0 && (
          <Typography
            variant="body2"
            sx={{ color: theme.palette.text.disabled, textAlign: "center", fontStyle: "italic", mt: 2 }}
          >
            Nenhum pedido neste estágio.
          </Typography>
        )}

        {!collapsed && restLocal > 0 && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 0.5 }}>
            <Chip
              size="small"
              label={`Mostrar mais (+${restLocal})`}
              onClick={() => setVisible((v) => v + 12)}
              clickable
            />
          </Box>
        )}
      </Box>
    </Paper>
  );
}
